/**
 * Hook for managing branding configuration with React Query
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useEffectiveCompanyId } from './useEffectiveCompany';
import { brandingApi } from '../services/brandingApi';
import { BrandingUpdate } from '../types/branding';

export const useBranding = () => {
  const queryClient = useQueryClient();
  const companyId = useEffectiveCompanyId();

  const {
    data: branding,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['branding', companyId],
    queryFn: () => brandingApi.getBranding(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  const updateMutation = useMutation({
    mutationFn: (data: BrandingUpdate) =>
      brandingApi.updateBranding(companyId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', companyId] });
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => brandingApi.uploadLogo(companyId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', companyId] });
    },
  });

  return {
    branding,
    isLoading,
    error,
    updateBranding: updateMutation.mutateAsync,
    uploadLogo: uploadLogoMutation.mutateAsync,
    isUpdating: updateMutation.isLoading,
    isUploading: uploadLogoMutation.isLoading,
  };
};
